import { BodyConfig } from 'lib/types';
import * as SVG from 'svg.js';
import { SALESFORCE_SANS } from 'lib/svgs';
import { CLOUD_ICON_STROKE } from '../data/data-source-icons';

type ConnectorConfig = {
	lineAttr: {
		stroke: string
		'stroke-dasharray': string
		'stroke-linecap': string
	}
	endAttr: {
		fill: string
		stroke: string
	}
	endRadius: number
	length: number
};

// Connects two elements with element 2 "rotating" around element 1
export class MoonConnector {
	planet: SVG.G;
	moon: Body;
	rotation: number;
	config: ConnectorConfig;
	links: SVG.G;
	link: SVG.G;
	line: SVG.Line;
	end1: SVG.Circle;
	end2: SVG.Circle;

	constructor(planet: SVG.G, moon: Body, links: SVG.G, config: ConnectorConfig, rotation: number = 0) {
		this.links = links;
		this.planet = planet;
		this.moon = moon;
		this.rotation = rotation;
		this.config = config;
		this.connect();
		this.update();
	}

	// Draws the initial connection between the two elements
	connect() {
		this.link = this.links.group();
		this.line = this.link.line(0, 0, 0, 0).attr(this.config.lineAttr); 
		this.end1 = this.link.circle(this.config.endRadius).attr(this.config.endAttr); 
		this.end2 = this.link.circle(this.config.endRadius).attr(this.config.endAttr);
	}

	getPlanetPosition() {
		const position = pos(this.planet);
		position.r = radius(this.planet);
		return position;
	}

	getMoonPosition() {
		const position = pos(this.moon);
		position.r = radius(this.moon);
		return position;  
	}

	// Updates the line taking the rotation around element1 into account
	update() {
		const planet = this.getPlanetPosition();
		const rotCenter = {x: planet.x + planet.r, y: planet.y + planet.r};
		const radianAngle = this.rotation * (Math.PI / 180);
		const endCapOffset = this.config.endRadius / 2;
		const cx = Math.cos(radianAngle);
		const cy = Math.sin(radianAngle);
		// FIXME: the sizes change based on their contents, so figure out a way to make them a predictable size,
		// or maybe do this by measuring the group first or something
		const moonCenter = {
			x: cx * (this.config.length + (planet.r * 2)) + planet.r,
			y: cy * (this.config.length + (planet.r * 2)) + planet.r + 12
		};
		const lineCenter = {
			x1: cx * planet.r + rotCenter.x,
			y1: cy * planet.r + rotCenter.y,
			x2: cx * (this.config.length + (planet.r)) + rotCenter.x,
			y2: cy * (this.config.length + (planet.r)) + rotCenter.y
		};
		const endCenters = {
			x1: cx * (planet.r + endCapOffset) + rotCenter.x,
			y1: cy * (planet.r + endCapOffset) + rotCenter.y,
			x2: cx * (this.config.length + (planet.r) - endCapOffset) + rotCenter.x,
			y2: cy * (this.config.length + (planet.r) - endCapOffset) + rotCenter.y
		};

		this.moon.center(moonCenter.x, moonCenter.y);
		this.line.attr(lineCenter);
		this.end1.center(endCenters.x1, endCenters.y1);
		this.end2.center(endCenters.x2, endCenters.y2);
	}
}

/** Some bullshit just for Parker's demo... basically, don't use this because its a hack */
class MoonMoonConnector extends MoonConnector {
	getPlanetPosition() {
		const position = pos(this.planet);
		position.r = radius(this.planet);
		position.y = Math.abs(position.y);

		// The extra special BS
		const rbox = this.planet.rbox();
		position.x = position.x + 260;
		position.y = position.y + (rbox.cy - 266);

		// tslint:disable-next-line:no-console
		console.log(this.planet.rbox());

		return position;
	}
}

type BodyEventHandler = (e: MouseEvent, c: BodyConfig) => void;
type HandlersBag = {
	click: BodyEventHandler[]
	mouseover: BodyEventHandler[]
	mouseout: BodyEventHandler[]
};

function createHandlerDispatcher(handlers: BodyEventHandler[], config: BodyConfig) {
	return function (evt: MouseEvent) {
		handlers.forEach(handler => handler(evt, config));
	};
}

/*
 * Body - a cellestial body in a constellation
 */
export class Body {
	config: BodyConfig;
	group: SVG.G;
	body: SVG.Circle;

	constructor (config: BodyConfig, container: SVG.G, handlers: HandlersBag) {
		this.config = config;
		this.group = container.group();
		this.group.click(createHandlerDispatcher(handlers.click, config));
		this.group.mouseover(createHandlerDispatcher(handlers.mouseover, config));
		this.group.mouseout(createHandlerDispatcher(handlers.mouseout, config));
		this.drawCircle();
		this.drawIcon();
		this.drawBadge();
		this.drawLabel();
	}

	drawCircle () {
		this.body = this.group
						.circle(this.config.radius)
						.fill(this.config.color)
						.addClass('svg-constellation_planet');
	}

	drawBadge () {
		if (this.config.badge) {
			const offset = 18;
			const badge = this.group.group();
			badge.svg(this.config.badge);
			badge.translate(this.config.radius - offset, this.config.radius - offset);
			badge.front();
		}
	}

	// Draws it below the circle and centered horizontally
	drawLabel () {
		const groupBox = this.group.bbox();
		const text = this.group.text(this.config.name).fill('#000');
		text.font({ family: 'Salesforce Sans', weight: 'bold'});
		const xOffset = (text.bbox().width - groupBox.width) / 2;
		
		text.translate(-xOffset, this.config.radius);
	}

	drawIcon () {
		this.group.svg(this.config.icon);
	}

	// This keeps the body centered on the body, not the group containing the body which changes size
	// with the text label and other baubles
	center (x: number, y: number) {
		const xOffset = (this.group.rbox().w - this.body.rbox().w) / 2;
		// tslint:disable-next-line:no-console
		// console.log(xOffset);
		this.group.center(x + xOffset, y);
	}

	transform () {
		return this.group.transform();
	}

	get node () {
		return this.group.node;
	}
}

/*
 * Moon - a body that orbits around another "center" body
 */
export class Moon extends Body {
	orbitee: SVG.G;

	constructor (config: BodyConfig, center: SVG.G, handlers: HandlersBag) {
		super(config, center, handlers);
		this.orbitee = center;
	}
}

/*
 * Constellation - a collection of a center body with "moon" bodies arranged in orbit around the center
 */
export class Constellation {
	handlers: HandlersBag;
	svg: SVG.Doc;
	nodes: SVG.G;
	links: SVG.G;
	bodyConfig: BodyConfig;
	moonConfigs: BodyConfig[];
	connectorConfig: ConnectorConfig;
	center: {x: number, y: number};

	constructor(el: HTMLElement, id: string, centerConfig: BodyConfig, moonConfigs: BodyConfig[]) {
		this.handlers = {
			click: [],
			mouseover: [],
			mouseout: [],
		};
		this.svg = SVG(el).size('100%', '100%');
		this.svg.attr({id, style: 'position: absolute; left: 0; opacity: 1; transition: left 0.5s, opacity 0.5s'});
		this.svg.defs().svg(SALESFORCE_SANS);
		this.svg.defs().svg(CLOUD_ICON_STROKE);
		this.nodes = this.svg.group();
		this.links = this.nodes.group();
		this.bodyConfig = centerConfig;
		this.moonConfigs = moonConfigs;
		const box = this.svg.rbox();
		this.center = {
			x: (box.width / 2) - (this.bodyConfig.radius / 2), 
			y: box.height < 400 ? 200 : (box.height / 2) - (this.bodyConfig.radius / 2)
		};
		this.connectorConfig = {
			lineAttr: {
				stroke: '#DDDBDA',
				'stroke-dasharray': '5, 5',
				'stroke-linecap': 'square'
			},
			endAttr: {
				fill: '#FFFFFF',
				stroke: '#DDDBDA'
			},
			endRadius: 7,
			length: 120
		};
	}

	onClick (handler: BodyEventHandler) {
		this.handlers.click.push(handler);
	}

	onMouseover (handler: BodyEventHandler) {
		this.handlers.mouseover.push(handler);
	}

	onMouseout (handler: BodyEventHandler) {
		this.handlers.mouseout.push(handler);
	}

	startLeft () {
		this.svg.attr({style: 'position: absolute; left: -400px; opacity: 0; z-index: -1;'});
	}

	slideLeft () {
		// tslint:disable-next-line:max-line-length
		this.svg.attr({style: 'position: absolute; left: -400px; opacity: 0; z-index: -1; transition: left 0.5s, opacity 0.5s'});
	}

	slideCenter() {
		this.svg.attr({style: 'position: absolute; left: 0px; opacity: 1; z-index: 1; transition: left 0.5s, opacity 0.5s'});
	}

	startRight() {
		// tslint:disable-next-line:max-line-length
		this.svg.attr({style: 'position: absolute; left: 400px; opacity: 0; z-index: -1;'});
	}

	slideRight() {
		// tslint:disable-next-line:max-line-length
		this.svg.attr({style: 'position: absolute; left: 400px; opacity: 0; z-index: -1; transition: left 0.5s, opacity 0.5s'});
	}

	render () {
		const center = this.nodes.group().center(this.center.x, this.center.y);
		const planet = new Body(this.bodyConfig, center, this.handlers);
		// tslint:disable-next-line:no-console
		console.log('here1 '  + planet);
		
		const total = this.moonConfigs.length;
		const inc = 360 / total;
		const connectors: MoonConnector[] = [];
		let s: Moon;
		let rotation;
		let config;
		let connectorConfig: ConnectorConfig;
		
		for (let i = 0; i < total; i++) {
			config = this.moonConfigs[i];

			// Tweak the connector for configured ones
			connectorConfig = JSON.parse(JSON.stringify(this.connectorConfig));
			if (config.configured) {
				connectorConfig.lineAttr['stroke-dasharray'] = '';
				connectorConfig.endAttr.fill = connectorConfig.endAttr.stroke;
			}

			s = new Moon(config, center, this.handlers);
			rotation = config.rotation ? config.rotation : inc * i;
			// tslint:disable-next-line:no-console
			console.log('rotation! ' + rotation + ' total is ' + total);
			if (total === 3 && rotation === 120) {
				rotation = 180;
			} else if (total === 3 && rotation === 240) {
				rotation = 270;
			} else if (total === 5 && rotation === 72) {
				rotation = 60;
			} else if (total === 5 && rotation === 144) {
				rotation = 120;
			} else if (total === 5 && rotation === 216) {
				rotation = 180;
			} else if (total === 5 && rotation === 288) {
				rotation = 270;
			} else if (total === 6 && rotation === 315) {
				rotation = 300;
			}

			connectors.push(new MoonConnector(center, s, this.links, connectorConfig, rotation));

			// Moons of moons are slightly smaller and closer to the moon
			if (config.moons) {
				const moonMoonConnectorConfig = JSON.parse(JSON.stringify(connectorConfig)) as ConnectorConfig;
				moonMoonConnectorConfig.length = 50;

				config.moons.forEach(moonConfig => {
					const mooMoo = new Moon(moonConfig, s.group, this.handlers);
					connectors.push(new MoonMoonConnector(s.group, mooMoo, this.links, moonMoonConnectorConfig, moonConfig.rotation));
				});
			}
		}
		
		// Fun animation 
		/*
		setInterval(
			() => {
				connectors.forEach((c) => {
				c.rotation++;
				if (c.rotation > 360) {
					c.rotation = 0;
				}
				c.update();
				});
			}, 
			50
		);
		// */
	}
}

// Returns a position object from the transform for an SVG object
function pos(el: SVG.G | Body) {
  const t = el.transform();
  return {x: t.x || 0, y: t.y || 0, r: 0};
}

// Returns the radius of the first circle in the group
function radius(group: SVG.G | Body) {
	const circle = group.node.querySelector('circle');
	const safeInt = circle ? circle.getAttribute('r') : null;
	return parseInt(safeInt || '0', 10);
}


// WEBPACK FOOTER //
// ./src/DataSources/Constellation.ts